import { Util } from '@libs/utilities/util';


/**------------------------------------------------------
 * Basic Statistics
 */
export class StatsBasicStatistics {

	/**------------------------------------------------------
	 * Distribution & Divination
	 */
	// distribution(dataset: number[]): InstancesDistribution;

	// standardDeviation(dataset: number[], usePopulation: boolean = false): numberInstancesDistribution;


	/**------------------------------------------------------
	 * Arithmetic Metrics
	 */
	average(dataset: number[]): number {
		this.checkNumberDataset(dataset);
		return dataset.reduce((x: number, y: number) => x + y, 0) / dataset.length;
	}

	median(dataset: number[]): number {
		this.checkNumberDataset(dataset);

		//0 - get the middle value
		const sorted: number[] = dataset.slice().sort((a: number, b: number) => a - b);
		const middle: number   = Math.floor(sorted.length / 2);

		//1 - return the median
		if (sorted.length % 2 === 0) return (sorted[middle - 1] + sorted[middle]) / 2;
		return sorted[middle];
	}

	mode(dataset: number[]): number[] {
		this.checkNumberDataset(dataset);

		//0 - create object with the occurrences
		const freq: Record<string, number> = {};
		for (const item of dataset) {
			if (freq[item]) freq[item]++;
				else freq[item] = 1;
		}

		//1 - calculate the mode
		let maxCount: number   = 0;
		let mode	: number[] = [];
		for (const item in freq) {

			//a. if another element is found with same occurrence, add it
			if (freq[item] === maxCount) mode.push(Util.Number.toNumber(item));

			//b. if we find a new max occurrence value
			if (freq[item] > maxCount) {
				maxCount = freq[item];
				mode	 = [Util.Number.toNumber(item)];
			}
		}

		return Util.Array.unique(mode);
	}

	sum(dataset: number[]): number {
		this.checkNumberDataset(dataset);
		return dataset.reduce((x: number, y: number) => x + y, 0);
	}


	/**------------------------------------------------------
	 * All Metrics Combined
	 */
	metrics(dataset: number[]): IStatsDatasetMetrics {
		this.checkNumberDataset(dataset);

		const metrics: IStatsDatasetMetrics = {
			avg 			: this.average(dataset),
			median 			: this.median(dataset),
			mode 			: this.mode(dataset),
			marginalValue 	: this.marginalValues(dataset),
			range 			: this.range(dataset),
			min 			: Math.min(...dataset),
			max 			: Math.max(...dataset),
			sum 			: this.sum(dataset)
		};
		return metrics;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	marginalValues(dataset: number[]): IStatsMarginalValues {
		this.checkNumberDataset(dataset);

		//0 - create initial min/max result object
		const result: IStatsMarginalValues = {
			min : Number.MAX_SAFE_INTEGER,
			max : -1
		};

		//1 - get the min/max values of the dataset
		for (const value of dataset) {
			if (value < result.min) result.min = value;
			if (value > result.max) result.max = value;
		}
		return result;
	}

	range(dataset: number[]): number {
		this.checkNumberDataset(dataset);
		const extremeValues: IStatsMarginalValues = this.marginalValues(dataset);
		return Math.abs(extremeValues.min - extremeValues.max);
	}

	distribution(dataset: number[]): IStatsDistribution {
		this.checkNumberDataset(dataset);

		//0 - calculate the distribution
		const distObject: Record<number, number> = {};
		for (let key of dataset) {
			if (key > 0) {
				key = Math.round(key);
				if (distObject?.[key]) distObject[key] += 1;
					else distObject[key] = 1;
			}
		}

		//1 - create the result
		const distribution: IStatsDistribution = {
			labels	: Object.keys(distObject),
			data	: Object.values(distObject)
		};
		return distribution;
	}


	/**------------------------------------------------------
	 * Calculation
	 */
	factorial(value: number): number {
		Util.Number.checkNumberPositive(value);
		return (value <= 1) ? 1 : value * this.factorial(value - 1);
	}

	//** Set of S of all subsets, including the empty set and S itself */
	powerset<T>(dataset: T[]): Array<T[]> {
		this.checkEmptyArray(dataset);
		return dataset.reduce(
			(subsets: Array<T[]>, value: T) => subsets.concat(
				subsets.map((set: T[]) => [value].concat(set))
			),
			[[]]
		);
	}

	//** Permutations of an array's elements */
	// > example :  [1, 33, 5]  =>  [ [ 1, 33, 5 ], [ 1, 5, 33 ], [ 33, 1, 5 ], [ 33, 5, 1 ], [ 5, 1, 33 ], [ 5, 33, 1 ] ]
	permutations<T>(dataset: T[]): Array<T[]> {

		//0 - are two or less elements in the array
		this.checkEmptyArray(dataset);
		if (dataset.length > 10) throw new Error(`UtilUnit => permutations => FATAL ERROR: datasets with over 10 elements will be to extensive for the computer (dataset.length: "${dataset.length}")`);
		if (dataset.length <= 2) {
			return (dataset.length === 2)
				? [dataset, [dataset[1], dataset[0]]]
				: [dataset];
		}

		//1 - create the permutations
		return dataset.reduce(
			(acc: Array<T[]>, item: T, i: number) => acc.concat(
				this.permutations([...dataset.slice(0, i), ...dataset.slice(i + 1)])
					.map((value: T[]) => [item, ...value])
			), []
		);
	}

	//** Checks if two numbers are approximately equal */
	approximatelyEqual(a: number, b: number, epsilon: number = 0.001): boolean {
		Util.Number.checkNumbers([a, b]);
		return Math.abs(a - b) < epsilon;
	}


	/**------------------------------------------------------
	 * Statistic Samples
	 * > source (sample)	 : https://decipher.dev/30-seconds-of-typescript/docs/sample
	 * > source (sampleSize) : https://decipher.dev/30-seconds-of-typescript/docs/sampleSize
	 */
	sample<T>(dataset: T[]): T {
		this.checkEmptyArray(dataset);
		return dataset[Math.floor(Math.random() * dataset.length)];
	}
	sampleSize<T>(dataset: T[], sampleSizeCount: number = 1): T[] {

		//0 - check the parameters
		this.checkEmptyArray(dataset);
		if (dataset.length < sampleSizeCount) throw new Error(`UtilUnit => sampleSize => FATAL ERROR: dataset is smaller then the sampleSizeCount (dataset.length: "${dataset.length}" / sampleSizeCount: "${sampleSizeCount}")`);

		//1 - get sample size
		let m: number = dataset.length;
		while (m) {
			const i: number = Math.floor(Math.random() * m--);
			[dataset[m], dataset[i]] = [dataset[i], dataset[m]];
		}
		return dataset.slice(0, sampleSizeCount);
	}


	/**------------------------------------------------------
	 * Standard Deviation
	 * > source  : https://decipher.dev/30-seconds-of-typescript/docs/standardDeviation/
	 * > example : [10, 2, 38, 23, 38, 23, 21]  =>  13.284434142114991 (sample)
	 */
	standardDeviation(dataset: number[], usePopulation: boolean = false): number {

		//0 - check the parameters
		this.checkNumberDataset(dataset);

		//1 - calculate the standard deviation
		const mean: number = dataset.reduce((acc: number, value: number) => acc + value, 0) / dataset.length;
		return Math.sqrt(
			dataset
				.reduce((acc: number[], value: number) => acc.concat((value - mean) ** 2), [])
				.reduce((acc: number, value: number)   => acc + value, 0) / (dataset.length - (usePopulation ? 0 : 1))
		);
	}


	/**------------------------------------------------------
	 * Mappings
	 */

	//** May Y Values to Dataset Points */
	createDataPoints(yArray: number[]): number[][] {

		//0 - define data points
		const dataPoints: number[][] = [];

		//1 - map the data points
		for (const yEntry of yArray.entries()) {
			dataPoints.push(yEntry);
		}

		//2 - returned mapped data points
		return dataPoints;
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private checkEmptyArray<T>(dataset: T[]) {
		if (Util.Array.isEmpty(dataset)) 	  throw new Error(`UtilMath => checkDataset => FATAL ERROR: the provided dataset is empty (dataset.length is "0")`);
	}
	private checkNumberDataset<T>(dataset: T[]) {
		if (Util.Array.isEmpty(dataset)) 	  throw new Error(`UtilMath => checkDataset => FATAL ERROR: the provided dataset is empty (dataset.length is "0")`);
		if (!Util.Number.areNumbers(dataset)) throw new Error(`UtilMath => checkDataset => FATAL ERROR: the provided dataset has elements which are not numbers (dataset: ${dataset})`);
	}
}


//** Interfaces --------------------------------- */
export interface IStatsDatasetMetrics {
	avg 			: number;
	median 			: number;
	mode 			: number[];
	marginalValue 	: IStatsMarginalValues;
	range 			: number;
	min 			: number;
	max 			: number;
	sum 			: number;
}

interface IStatsMarginalValues {
	min 	: number;
	max 	: number;
}

interface IStatsDistribution {
	labels	: string[];
	data	: number[];
}
